
import { computed, defineComponent, nextTick, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import moment from "moment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { hasFeature } from "@/core/helpers/feature";
import { useLuxChat } from "@/modern/composables/useLuxChat";
import LuxAvatar from "@/modern/components/lux/LuxAvatar.vue";
import LuxComposer from "@/modern/components/lux/LuxComposer.vue";
import LuxMessage from "@/modern/components/lux/LuxMessage.vue";
import { LuxContextType } from "@/modern/types/lux";

export default defineComponent({
  name: "lux-widget",
  components: { LuxAvatar, LuxComposer, LuxMessage },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const chat = useLuxChat();

    const allowed = computed(() => hasFeature("lux-ai"));
    const open = ref(false);

    // Só aparece depois que o config confirma que a Lux está ligada no backoffice,
    // para não piscar na tela e sumir em seguida.
    const visible = computed(() => allowed.value && chat.config.value?.enabled === true);

    // Reconsulta o config ao navegar, no máximo uma vez por minuto (mesmo cache
    // da API): desligar no backoffice tira o widget sem recarregar a página.
    const CONFIG_TTL_MS = 60 * 1000;
    let configCheckedAt = 0;

    async function refreshConfig(force = false) {
      if (!allowed.value) return;
      if (!force && Date.now() - configCheckedAt < CONFIG_TTL_MS) return;
      configCheckedAt = Date.now();
      try {
        await chat.loadConfig();
      } catch (e) {
        // Sem config não dá para afirmar que está ligada: some até a próxima checagem.
        chat.config.value = null;
      }
      if (!visible.value) {
        open.value = false;
      }
    }

    watch(allowed, (value) => {
      if (value) refreshConfig(true);
    }, { immediate: true });

    watch(() => route.path, () => refreshConfig());
    const view = ref<"chat" | "history">("chat");
    const loaded = ref(false);

    const scroller = ref<HTMLElement | null>(null);
    const composer = ref<InstanceType<typeof LuxComposer> | null>(null);

    /**
     * Contexto da tela atual, derivado da rota.
     *
     * Como o widget vive em todas as telas, ele descobre sozinho sobre o que o
     * aluno provavelmente quer falar — sem precisar de botão em cada página.
     */
    function currentContext(): { type: LuxContextType; id: number | null } {
      const path = route.path;
      const idOf = (value: unknown) => {
        const parsed = parseInt(String(value ?? ""), 10);
        return Number.isFinite(parsed) ? parsed : null;
      };

      if (path.includes("/training-machines/") || path.includes("/competition-machines/")) {
        return { type: "machine", id: idOf(route.params.machine_uuid ?? route.params.id) };
      }

      if (path.includes("/challenges/")) {
        return { type: "challenge", id: idOf(route.params.id) };
      }

      if (route.params.lesson_id) {
        return { type: "lesson", id: idOf(route.params.lesson_id) };
      }

      if (path.includes("/learning-paths/")) {
        return { type: "pathway", id: idOf(route.params.id) };
      }

      if (path.includes("/training/course/")) {
        return { type: "course", id: idOf(route.params.id) };
      }

      return { type: "general", id: null };
    }

    function scrollToBottom() {
      nextTick(() => {
        if (scroller.value) {
          scroller.value.scrollTop = scroller.value.scrollHeight;
        }
      });
    }

    async function toggle() {
      open.value = !open.value;

      if (!open.value) {
        return;
      }

      // Conversas carregam uma única vez, na primeira abertura: montar o widget
      // em toda tela não pode custar requisições extras por navegação.
      if (!loaded.value) {
        try {
          await chat.loadConversations();
          loaded.value = true;
        } catch (e) {
          return;
        }
      }

      view.value = "chat";
      scrollToBottom();
    }

    async function onSend(content: string) {
      // Conversa nova nasce amarrada à tela em que o aluno está.
      if (!chat.activeUuid.value) {
        const { type, id } = currentContext();
        await chat.startConversation(type, id);
      }

      const failedText = await chat.sendMessage(content);

      if (failedText) {
        composer.value?.restore(failedText);
      }

      scrollToBottom();
    }

    /**
     * Leva a conversa atual para a página cheia. Ela é retomada lá pelo
     * ?conversation=<uuid>, que index.vue já sabe abrir.
     */
    function openFullscreen() {
      const query = chat.activeUuid.value ? { conversation: chat.activeUuid.value } : {};
      open.value = false;
      router.push({ path: "/apps/lux", query });
    }

    async function onNewConversation() {
      const { type, id } = currentContext();
      await chat.startConversation(type, id);
      view.value = "chat";
      scrollToBottom();
    }

    async function onOpenConversation(uuid: string) {
      await chat.openConversation(uuid);
      view.value = "chat";
      scrollToBottom();
    }

    function confirmDelete(uuid: string) {
      Swal.fire({
        title: "Apagar esta conversa?",
        text: "Não dá para desfazer.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Apagar",
        cancelButtonText: "Cancelar",
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-light",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          chat.removeConversation(uuid);
        }
      });
    }

    const formatDate = (value: string | null) =>
      value ? moment(value).format("DD/MM HH:mm") : "";

    watch(() => chat.messages.value.length, scrollToBottom);

    return {
      ...chat,
      allowed,
      visible,
      open,
      view,
      scroller,
      composer,
      toggle,
      onSend,
      openFullscreen,
      onNewConversation,
      onOpenConversation,
      confirmDelete,
      formatDate,
    };
  },
});
